import { useNavigate, useParams } from "react-router-dom";
import UtilityPageLayout from "../../../component/layout/UtilityPageLayout";
import styles from "./ServiceRequesInstancesPage.module.scss";
import Filter from "../../../../../common/components/xepp/table-filter/table-filter-components";
import DataTable, { ColumnType } from "../../../../../common/components/xepp/table/DataTable";
import EmptyState from "../../../../../common/components/xepp/empty-state/EmptyState";
import { EyeIcon, FilterIcon2, ServiceRequestsIcon } from "../../../../../assets/icons";
import { ServiceRequestInstance, ServiceRequestPaymentStatusMap, ServiceRequestStatusMap } from "../../../../../model/service-request/service-request-instance";
import { TablePagination } from "../../../../../common/components/xepp/table-pagination/TablePagination";
import Button from "../../../../../common/components/xepp/button/Button";
import { useEffect, useState } from "react";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { Range } from "react-date-range";
import { useLazyGetServiceRequestInstancesQuery, useLazyGetServiceRequestQuery } from "../../../../../service/customersupport-service";
import { getErrorMessage } from "../../../../../utils/error-handler";
import { FilterItem } from "../../../../../common/components/xepp/table-filter/table-filter.type";
import { formatTimestamp } from "../../../../../utils";
import SearchableMultiselectDropdown from "../../../../../common/components/xepp/searchable-multiselect-dropdown/SearchableMultiselectDropdown";
import { useLazyGetCustomerForUtilityAdminQuery } from "../../../../../service/customer-service";
import ServiceRequestInstanceCard from "./ServiceRequestInstanceCard";

const ServiceRequestInstancesPage = () => {
	let { id = "" } = useParams();
	const navigator = useNavigate();

	const [searchTerm, setSearchTerm] = useState<string>("");
	const debouncedSearchTerm = useDebounce(searchTerm, 500);
	const [Page, setPage] = useState(1);
	const PageSize = 15;

	const now = new Date();
	const startDate = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);

	const [isFilterOpen, setIsFilterOpen] = useState(false);
	const [selectedPaymentStatus, setSelectedPaymentStatus] = useState<string>("");
	const [selectedStatus, setSelectedStatus] = useState<string>("");
	const [checkedIds, setCheckedIds] = useState<string[]>([]);

	const [dateRange, setDateRange] = useState<Range>({
		startDate: startDate,
		endDate: now,
		key: "selection",
	});
	const [appliedFilters, setAppliedFilters] = useState<{
		PaymentStatus?: string;
		Status?: string;
		DateFrom?: string;
		DateTo?: string;
		CustomerIds?: string[];
	}>({
		PaymentStatus: "",
		Status: "",
		DateFrom: undefined,
		DateTo: undefined,
		CustomerIds: undefined,
	});

	const [getServiceRequestInstances, { data, isLoading, isFetching }] = useLazyGetServiceRequestInstancesQuery();
	const [getServiceRequestDefinition, { data: serviceRequestDefiniton }] = useLazyGetServiceRequestQuery();
	const [getCustomers, { data: customersPaged }] = useLazyGetCustomerForUtilityAdminQuery();

	const { count } = data ?? {};

	useEffect(() => {
		getServiceRequestDefinition(id);
	}, [id, getServiceRequestDefinition]);

	useEffect(() => {
		const filterParams = {
			RequestTypeId: serviceRequestDefiniton?.requestTypeId ?? "",
			Page,
			PageSize,
			...(appliedFilters.PaymentStatus && { PaymentStatus: appliedFilters.PaymentStatus }),
			...(appliedFilters.Status && { Status: appliedFilters.Status }),
			...(appliedFilters.CustomerIds && { CustomerIds: appliedFilters.CustomerIds }),
			...(appliedFilters.DateFrom && { DateFrom: appliedFilters.DateFrom }),
			...(appliedFilters.DateTo && { DateTo: appliedFilters.DateTo }),
		};

		getServiceRequestInstances(filterParams)
			.unwrap()
			.catch(err => getErrorMessage(err.data));
	}, [serviceRequestDefiniton?.requestTypeId, Page, PageSize, appliedFilters, getServiceRequestInstances]);

	useEffect(() => {
		const filterParams = {
			page: 1,
			pageSize: 15,
			search: debouncedSearchTerm,
		};

		getCustomers(filterParams)
			.unwrap()
			.catch(err => getErrorMessage(err.data));
	}, [debouncedSearchTerm, getCustomers]);

	useEffect(() => {
		if (searchTerm !== debouncedSearchTerm && searchTerm !== "") {
			setPage(1);
		}
	}, [searchTerm, debouncedSearchTerm]);

	const handleSelectionChange = (selectedId: string) => {
		setCheckedIds(prev => {
			const updated = prev.includes(selectedId) ? prev.filter(x => x !== selectedId) : [...prev, selectedId];

			setAppliedFilters({
				...appliedFilters,
				CustomerIds: updated.length === 0 ? undefined : updated,
			});

			return updated;
		});
	};

	const handleApplyFilter = () => {
		setAppliedFilters({
			...appliedFilters,
			PaymentStatus: selectedPaymentStatus,
			Status: selectedStatus,
			DateFrom: dateRange.startDate ? dateRange.startDate.toISOString() : undefined,
			DateTo: dateRange.endDate ? dateRange.endDate.toISOString() : undefined,
		});

		setIsFilterOpen(false);
		setPage(1);
	};

	const handleResetFilter = () => {
		setSelectedPaymentStatus("");
		setSelectedStatus("");
		setIsFilterOpen(false);
		setCheckedIds([]);

		setDateRange({
			startDate: startDate,
			endDate: now,
			key: "selection",
		});

		setAppliedFilters({
			PaymentStatus: undefined,
			Status: undefined,
			DateFrom: startDate.toISOString(),
			DateTo: now.toISOString(),
			CustomerIds: undefined,
		});

		setPage(1);
	};

	const handleOpenFilter = () => {
		setIsFilterOpen(true);
	};

	const handleCloseFilter = () => {
		setIsFilterOpen(false);
	};

	const handleDateRangeChange = (ranges: any) => {
		setDateRange(ranges.selection);
	};

	const handlePaymentStatusChange = (value: string) => {
		setSelectedPaymentStatus(value);
	};

	const handleStatusChange = (value: string) => {
		setSelectedStatus(value);
	};

	const statusClassMap: Record<string, string> = {
		Unknown: styles.circleBlack,
		Free: styles.circleGray,
		Unpaid: styles.circleRed,
		Paid: styles.circleGreen,
		Completed: styles.circleGreen,
		InProgress: styles.circleYellow,
		Assigned: styles.circleBlack,
		Unassigned: styles.circleRed,
	};

	const filterItems: FilterItem[] = [
		{
			id: "date_range",
			label: "DATE RANGE",
			type: "date",
			initialDateRange: dateRange,
			onChange: handleDateRangeChange,
		},
		{
			id: "payment_status",
			label: "Payment STATUS",
			type: "radio",
			options: [
				{
					id: "unknown",
					label: "Unknown",
					value: "Unknown",
					isSelected: selectedPaymentStatus === "Unknown",
				},
				{
					id: "free",
					label: "Free",
					value: "Free",
					isSelected: selectedPaymentStatus === "Free",
				},
				{
					id: "paid",
					label: "Paid",
					value: "Paid",
					isSelected: selectedPaymentStatus === "Paid",
				},
				{
					id: "unpaid",
					label: "Unpaid",
					value: "Unpaid",
					isSelected: selectedPaymentStatus === "Unpaid",
				},
			],
			onChange: handlePaymentStatusChange,
		},
		{
			id: "status",
			label: "STATUS",
			type: "radio",
			options: [
				{
					id: "status_completed",
					label: "Completed",
					value: "Completed",
					isSelected: selectedStatus === "Completed",
				},
				{
					id: "status_inProgress",
					label: "In Progress",
					value: "InProgress",
					isSelected: selectedStatus === "InProgress",
				},
				{
					id: "status_assigned",
					label: "Assigned",
					value: "Assigned",
					isSelected: selectedStatus === "Assigned",
				},
				{
					id: "status_unassigned",
					label: "Unassigned",
					value: "Unassigned",
					isSelected: selectedStatus === "Unassigned",
				},
			],
			onChange: handleStatusChange,
		},
	];

	const columns: Array<ColumnType<ServiceRequestInstance>> = [
		{
			header: "Date & Time",
			accessor: "createdAt",
			render: (record: ServiceRequestInstance) => <span>{formatTimestamp(record.createdAt ?? "")}</span>,
		},
		{
			accessor: "user",
			header: "Name of Customer",
			render: (record: ServiceRequestInstance) => (
				<span>
					{record.customerFirstName} {record.customerLastName}
				</span>
			),
		},
		{
			accessor: "Payment Status",
			header: "Payment Status",
			render: (record: ServiceRequestInstance) => (
				<div className={styles.statusWrapper}>
					<div className={`${styles.circle} ${statusClassMap[ServiceRequestPaymentStatusMap[record.paymentStatus]]}`} />
					<span className={styles.ellipsisText}>{ServiceRequestPaymentStatusMap[record.paymentStatus]}</span>
				</div>
			),
		},
		{
			accessor: "Status",
			header: "Status",
			render: (record: ServiceRequestInstance) => (
				<div className={styles.statusWrapper}>
					<div className={`${styles.circle} ${statusClassMap[ServiceRequestStatusMap[record.status]]}`} />
					<span className={styles.ellipsisText}>{ServiceRequestStatusMap[record.status]}</span>
				</div>
			),
		},
		{
			accessor: "id",
			header: "",
			render: (record: ServiceRequestInstance) => (
				<Button leftIcon={<EyeIcon className={styles.icon} />} variant="text" text="View" onClick={() => navigator(String(record.id))} />
			),
		},
	];

	// Header actions component
	const headerActions = (
		<>
			<SearchableMultiselectDropdown
				items={
					customersPaged?.items.map(c => {
						return { id: c.userId, label: `${c.firstName} ${c.lastName}` };
					}) ?? []
				}
				checkedIds={checkedIds}
				onSearch={(value: string) => setSearchTerm(value)}
				onSelectionChange={handleSelectionChange}
				placeholder="Search customers..."
			/>
			<Button leftIcon={<FilterIcon2 className={styles.icon} />} text="Add Filter" variant="outline" type="button" onClick={handleOpenFilter} />
		</>
	);

	return (
		<>
			<UtilityPageLayout title={serviceRequestDefiniton?.name ?? ""}>
				<div className={styles.serviceRequestInstancesContainer}>
					<Filter
						filterItems={filterItems}
						onApplyFilter={handleApplyFilter}
						onResetFilter={handleResetFilter}
						onClose={handleCloseFilter}
						isOpen={isFilterOpen}
					/>

					{/* Search + filter — always visible */}
					<div className={styles.toolbar}>
						{headerActions}
					</div>

					{/* Desktop table */}
					<div className={styles.tableWrapper}>
						<DataTable<any>
							data={data?.items ?? []}
							loading={isLoading}
							isUpdating={isFetching}
							columns={columns}
							emptyComponent={<EmptyState icon={<ServiceRequestsIcon />} title="No service request submitions" />}
						/>
					</div>

					{/* Mobile card list */}
					<div className={styles.cardList}>
						{(data?.items ?? []).length === 0 && !isLoading ? (
							<EmptyState icon={<ServiceRequestsIcon />} title="No service request submitions" />
						) : (data?.items ?? []).map((record: ServiceRequestInstance) => (
							<ServiceRequestInstanceCard
								key={record.id}
								record={record}
								statusClassMap={statusClassMap}
								onClick={() => navigator(String(record.id))}
							/>
						))}
					</div>

					<div className={styles.paginationWrapper}>
						<TablePagination isLoading={isFetching} totalItems={count ?? 0} itemsPerPage={PageSize} currentPage={Page} onPageChange={setPage} />
					</div>
				</div>
			</UtilityPageLayout>
		</>
	);
};

export default ServiceRequestInstancesPage;
